import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import axios from './api'
import { _ElMessage } from '@/utils/element-plus-wrapper'
import {
  type UserListItem,
  type UserWithLocalCredential,
  type UserWithLoginMethods,
  type UserResponseDto,
  type GetUsersQuery,
  type CreateUserPayload,
  type UpdateUserPayload,
  type UpdateUserAccessPayload,
  type CustomerNote,
  type CreateCustomerNotePayload,
  type UpdateCustomerNotePayload,
  type UserPreferences,
} from '@/types/user.type'
import type { PaginationMeta } from '@/types/pagination.type'
interface State {
  users: UserListItem[]
  users_stats: PaginationMeta | null
  currentUser: UserWithLocalCredential | null
  userDetail: UserWithLoginMethods | null
  customerNotes: CustomerNote[]
}

export const useUserStore = defineStore('userStore', {
  state: (): State => ({
    users: [],
    users_stats: null,
    currentUser: null,
    userDetail: null,
    customerNotes: [],
  }),
  getters: {
    get_current_user(state): UserWithLocalCredential {
      if (!state.currentUser) {
        throw new Error('Missing: Login user.')
      }
      return state.currentUser
    },
    // null は所属支店なし
    get_current_user_branch_org_id(): number | null {
      return this.get_current_user.branchOrgId
    },
    // null は担当コードなし
    get_current_user_salesrep_number(): number | null {
      return this.get_current_user.salesrepNumber
    },
  },
  actions: {
    // users
    clearUsers() {
      this.$patch((state) => {
        state.users = []
        state.users_stats = null
      })
    },
    clearCurrentUser() {
      this.$patch((state) => {
        state.currentUser = null
      })
      Sentry.setUser(null)
    },
    setCurrentUser(user: UserWithLocalCredential) {
      this.$patch((state) => {
        state.currentUser = user
      })
      Sentry.setUser({
        id: String(user.id),
        username: user.name,
        org: user.branch?.org,
        canAccessAllBranches: user.canAccessAllBranches,
      })
    },
    getUsers(query: GetUsersQuery) {
      axios
        .get<UserResponseDto>('/users', { params: query })
        .then((res) => {
          this.$patch((state) => {
            state.users = res.data.items
            state.users_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load users.' })
          console.error('Failed to fetch users:', err)
        })
    },
    updateMyPreferences(patch: Partial<UserPreferences>) {
      // 更新は preferences カラムを丸ごと置換するため、現在値に patch を merge して全項目を送る
      const preferences: UserPreferences = { ...this.get_current_user.preferences, ...patch }
      axios
        .put<UserWithLocalCredential>('/users/me/preferences', { preferences })
        .then((res) => {
          this.$patch((state) => {
            state.currentUser = res.data
          })
          _ElMessage({ type: 'success', message: 'Settings updated.' })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to update user settings.' })
          console.error('Failed to update user preferences:', err)
        })
    },
    // 変えるのはローカルログインのパスワード。localCredential が無い User は 404
    updateMyPassword(password: string) {
      return new Promise<void>((resolve, reject) => {
        axios
          .put('/users/me/password', { password })
          .then(() => resolve())
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to update password.' })
            console.error('Failed to update password:', err)
            reject(err)
          })
      })
    },
    // ユーザー管理
    clearUserDetail() {
      this.$patch((state) => {
        state.userDetail = null
      })
    },
    getUserDetail(id: number) {
      return new Promise<void>((resolve, reject) => {
        axios
          .get<UserWithLoginMethods>(`/users/${id}`)
          .then((res) => {
            this.$patch((state) => {
              state.userDetail = res.data
            })
            resolve()
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to load user details.' })
            console.error('Failed to fetch user:', err)
            reject(err)
          })
      })
    },
    createUser(payload: CreateUserPayload) {
      return new Promise<void>((resolve, reject) => {
        axios
          .post<UserWithLoginMethods>('/users', payload)
          .then(() => {
            _ElMessage({ type: 'success', message: 'User created.' })
            resolve()
          })
          .catch((err) => {
            const msg = err.response?.data?.message || 'Failed to create user.'
            _ElMessage({ type: 'error', message: msg })
            console.error('Failed to create user:', err)
            reject(err)
          })
      })
    },
    // WeConnect の紐付けがある User では name は無視され、email だけが反映される
    updateUser(id: number, payload: UpdateUserPayload) {
      return this.mutateUser(
        axios.put<UserWithLoginMethods>(`/users/${id}`, payload),
        'User updated.',
      )
    },
    // WeConnect の紐付けがある User では、System admin 以外の権限・branchOrgId・salesrepNumber は無視され、canAccessAllBranches は isSystemAdmin と紐付けから決め直される
    updateUserAccess(id: number, payload: UpdateUserAccessPayload) {
      return this.mutateUser(
        axios.put<UserWithLoginMethods>(`/users/${id}/access`, payload),
        'Access updated.',
      )
    },
    deactivateUser(id: number) {
      return this.mutateUser(
        axios.post<UserWithLoginMethods>(`/users/${id}/deactivate`),
        'User deactivated.',
      )
    },
    // ログイン ID は User の email
    addLocalCredential(id: number, password: string) {
      return this.mutateUser(
        axios.post<UserWithLoginMethods>(`/users/${id}/local-credential`, { password }),
        'Local sign-in added.',
      )
    },
    // 一覧の行は、更新後に絞り込み条件から外れることがあるので差し替えない
    mutateUser(request: Promise<{ data: UserWithLoginMethods }>, successMessage: string) {
      return new Promise<void>((resolve, reject) => {
        request
          .then((res) => {
            this.$patch((state) => {
              if (state.userDetail?.id === res.data.id) state.userDetail = res.data
              if (state.currentUser?.id === res.data.id) state.currentUser = res.data
            })
            _ElMessage({ type: 'success', message: successMessage })
            resolve()
          })
          .catch((err) => {
            const msg = err.response?.data?.message || 'Failed to update user.'
            _ElMessage({ type: 'error', message: msg })
            console.error('Failed to update user:', err)
            reject(err)
          })
      })
    },
    resetUserPassword(id: number, password: string) {
      return new Promise<void>((resolve, reject) => {
        axios
          .put(`/users/${id}/password`, { password })
          .then(() => {
            _ElMessage({ type: 'success', message: 'Password updated.' })
            resolve()
          })
          .catch((err) => {
            const msg = err.response?.data?.message || 'Failed to update password.'
            _ElMessage({ type: 'error', message: msg })
            console.error('Failed to reset user password:', err)
            reject(err)
          })
      })
    },
    // customer notes
    clearCustomerNotes() {
      this.$patch((state) => {
        state.customerNotes = []
      })
    },
    getCustomerNotes() {
      axios
        .get<CustomerNote[]>('/users/me/customer-notes')
        .then((res) => {
          this.$patch((state) => {
            state.customerNotes = res.data
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customer notes.' })
          console.error('Failed to fetch customer notes:', err)
        })
    },
    createCustomerNote(payload: CreateCustomerNotePayload) {
      axios
        .post<CustomerNote>('/users/me/customer-notes', payload)
        .then((res) => {
          this.$patch((state) => {
            state.customerNotes.push(res.data)
          })
          _ElMessage({ type: 'success', message: 'Customer note created.' })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to create customer note.' })
          console.error('Failed to create customer note:', err)
        })
    },
    updateCustomerNote(id: number, payload: UpdateCustomerNotePayload) {
      axios
        .put<CustomerNote>(`/users/me/customer-notes/${id}`, payload)
        .then((res) => {
          this.$patch((state) => {
            const index = state.customerNotes.findIndex((note) => note.id === id)
            if (index !== -1) {
              state.customerNotes[index] = res.data
            }
          })
          _ElMessage({ type: 'success', message: 'Customer note updated.' })
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            _ElMessage({
              type: 'warning',
              message:
                'This customer note no longer exists. It may have been deleted on another device.',
            })
            this.getCustomerNotes()
          } else {
            _ElMessage({ type: 'error', message: 'Failed to update customer note.' })
            console.error('Failed to update customer note:', err)
          }
        })
    },
    deleteCustomerNote(id: number) {
      axios
        .delete<CustomerNote>(`/users/me/customer-notes/${id}`)
        .then(() => {
          this.$patch((state) => {
            state.customerNotes = state.customerNotes.filter((note) => note.id !== id)
          })
          _ElMessage({ type: 'success', message: 'Customer note deleted.' })
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            _ElMessage({
              type: 'warning',
              message:
                'This customer note no longer exists. It may have been deleted on another device.',
            })
            this.getCustomerNotes()
          } else {
            _ElMessage({ type: 'error', message: 'Failed to delete customer note.' })
            console.error('Failed to delete customer note:', err)
          }
        })
    },
  },
})
